/**
 * 格式化警报消息为用户友好的文本
 * @param {string} alertText - 原始警报文本
 * @returns {string} 格式化后的用户友好警报文本
 */
export const formatAlertMessage = (t, alertText) => {
  // 定义数据类型的友好名称映射
  const dataTypeMap = {
    battery: t("dataType.battery"),
    soilhumid: t("dataType.soilhumid"),
    waterlevel: t("dataType.waterlevel"),
    airtemp: t("dataType.airtemp"),
    airhumid: t("dataType.airhumid"),
    light: t("dataType.light"),
    pm10: t("dataType.pm10"),
    pm2_5: t("dataType.pm2_5"),
    volt: t("dataType.volt"),
    soiltemp: t("dataType.soiltemp"),
    connection: t("dataType.connection"),
    tds: t("dataType.tds"),
    temp: t("dataType.temp"),
  };

  // 定义警报类型的友好名称映射
  const alertTypeMap = {
    alert: t("alertType.alert"),
    warning: t("alertType.warning"),
    info: t("alertType.info"),
  };

  // 定义单位映射
  const unitMap = {
    battery: t("unit.battery"),
    soilhumid: t("unit.soilhumid"),
    waterlevel: t("unit.waterlevel"),
    airtemp: t("unit.airtemp"),
    airhumid: t("unit.airhumid"),
    light: t("unit.light"),
    pm10: t("unit.pm10"),
    pm2_5: t("unit.pm2_5"),
    volt: t("unit.volt"),
    soiltemp: t("unit.soiltemp"),
    tds: t("unit.tds"),
    temp: t("unit.temp"),
  };

  // 定义比较文本映射
  const comparisonTextMap = {
    "<": t("comparisonText.below"),
    ">": t("comparisonText.above"),
    "<=": t("comparisonText.belowOrEqual"),
    ">=": t("comparisonText.aboveOrEqual"),
  };

  // 尝试解析各种警报格式
  try {
    // 格式1: "battery alert: 0 (<1); since 2025-04-24 14:53:47"
    const pattern1 = /(\w+)\s+(\w+):\s*([\d\.]+)\s*\(([<>]=?)([\d\.]+)\);\s*since\s+(.+)/;
    const match1 = alertText.match(pattern1);
    if (match1) {
      const [, dataType, alertType, currentValue, operator, threshold, time] = match1;

      const friendlyDataType = dataTypeMap[dataType] || dataType;
      const friendlyAlertType = alertTypeMap[alertType] || alertType;
      const unit = unitMap[dataType] || "";

      const comparisonText = comparisonTextMap[operator] || operator;

      // 特殊处理土壤湿度，将阈值转换为百分比
      let formattedThreshold = threshold;
      if (dataType === "soilhumid") {
        formattedThreshold = (parseFloat(threshold) * 10).toFixed(1);
      }

      // 特殊处理电池电量
      let formattedValue = currentValue;
      if (dataType === "battery") {
        formattedValue = parseFloat(currentValue).toFixed(1);
      }

      return t("alertFormat1", {
        friendlyDataType,
        friendlyAlertType,
        formattedValue,
        unit,
        comparisonText,
        formattedThreshold,
        time,
      });
    }

    // 格式2: "connection alert; no record since 2025-04-24 22:53:47"
    const pattern2 = /(\w+)\s+(\w+);\s*no record since\s+(.+)/;
    const match2 = alertText.match(pattern2);
    if (match2) {
      const [, dataType, alertType, time] = match2;

      const friendlyDataType = dataTypeMap[dataType] || dataType;
      const friendlyAlertType = alertTypeMap[alertType] || alertType;

      return t("alertFormat2", {
        friendlyDataType,
        friendlyAlertType,
        time,
      });
    }

    // 格式3: "soilhumid alert: 0; since 2025-04-24 14:53:47"
    const pattern3 = /(\w+)\s+(\w+):\s*([\d\.]+);\s*since\s+(.+)/;
    const match3 = alertText.match(pattern3);
    if (match3) {
      const [, dataType, alertType, value, time] = match3;

      const friendlyDataType = dataTypeMap[dataType] || dataType;
      const friendlyAlertType = alertTypeMap[alertType] || alertType;
      const unit = unitMap[dataType] || "";

      // 特殊处理土壤湿度，将值转换为百分比
      let formattedValue = value;
      if (dataType === "soilhumid") {
        formattedValue = (parseFloat(value) * 10).toFixed(1);
      }

      // 特殊处理电池电量
      if (dataType === "battery") {
        formattedValue = parseFloat(value).toFixed(1);
      }

      return t("alertFormat3", {
        friendlyDataType,
        friendlyAlertType,
        formattedValue,
        unit,
        time,
      });
    }

    // 格式1: "battery alert: 0 (<1)"
    const pattern4 = /(\w+)\s+(\w+):\s*([\d\.]+)\s*\(([<>]=?)([\d\.]+)\)/;
    const match4 = alertText.match(pattern4);
    if (match4) {
      const [, dataType, alertType, currentValue, operator, threshold, time] = match4;

      const friendlyDataType = dataTypeMap[dataType] || dataType;
      const friendlyAlertType = alertTypeMap[alertType] || alertType;
      const unit = unitMap[dataType] || "";

      const comparisonText = comparisonTextMap[operator] || operator;

      // 特殊处理土壤湿度，将阈值转换为百分比
      let formattedThreshold = threshold;
      if (dataType === "soilhumid") {
        formattedThreshold = (parseFloat(threshold) * 10).toFixed(1);
      }

      // 特殊处理电池电量
      let formattedValue = currentValue;
      if (dataType === "battery") {
        formattedValue = parseFloat(currentValue).toFixed(1);
      }

      return t("alertFormat4", {
        friendlyDataType,
        friendlyAlertType,
        formattedValue,
        unit,
        comparisonText,
        formattedThreshold,
        time,
      });
    }

    // 如果无法解析特定格式，返回原始文本
    return alertText;
  } catch (error) {
    console.error("Error formatting alert message:", error);
    return alertText;
  }
};
